import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { DiagramComponent } from "@/types/diagram";

const MAX_HISTORY_LENGTH = 10;

interface HistoryContextType {
  // State
  history: DiagramComponent[][];
  currentIndex: number;
  canUndo: boolean;
  canRedo: boolean;

  // Actions
  addHistory: (components: DiagramComponent[]) => void;
  undo: () => DiagramComponent[] | null;
  redo: () => DiagramComponent[] | null;
  clearHistory: () => void;
  replaceCurrentState: (components: DiagramComponent[]) => void;
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

interface HistoryProviderProps {
  children: ReactNode;
  initialState?: DiagramComponent[];
}

export const HistoryProvider: React.FC<HistoryProviderProps> = ({
  children,
  initialState = [],
}) => {
  const [history, setHistory] = useState<DiagramComponent[][]>([initialState]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  const addHistory = useCallback(
    (components: DiagramComponent[]) => {
      setHistory((prevHistory) => {
        // Remove any future history if we're not at the end
        const newHistory = [
          ...prevHistory.slice(0, currentIndex + 1),
          components,
        ];

        // Trim history if it exceeds max length
        const trimmedHistory =
          newHistory.length > MAX_HISTORY_LENGTH
            ? newHistory.slice(newHistory.length - MAX_HISTORY_LENGTH)
            : newHistory;

        // Update the current index to point to the newly added entry
        setCurrentIndex(trimmedHistory.length - 1);

        return trimmedHistory;
      });
    },
    [currentIndex]
  );

  const undo = useCallback((): DiagramComponent[] | null => {
    if (!canUndo) return null;

    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    return history[newIndex];
  }, [canUndo, currentIndex, history]);

  const redo = useCallback((): DiagramComponent[] | null => {
    if (!canRedo) return null;

    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    return history[newIndex];
  }, [canRedo, currentIndex, history]);

  const clearHistory = useCallback(() => {
    setHistory([[]]);
    setCurrentIndex(0);
  }, []);

  const replaceCurrentState = useCallback(
    (components: DiagramComponent[]) => {
      setHistory((prevHistory) => {
        const newHistory = [...prevHistory];
        newHistory[currentIndex] = components;
        return newHistory;
      });
    },
    [currentIndex]
  );

  const value: HistoryContextType = {
    // State
    history,
    currentIndex,
    canUndo,
    canRedo,

    // Actions
    addHistory,
    undo,
    redo,
    clearHistory,
    replaceCurrentState,
  };

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
};

export const useHistory = (): HistoryContextType => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error("useHistory must be used within a HistoryProvider");
  }
  return context;
};
